// Liste filtrée et rédaction à la demande du studio, en français et en anglais.
import { useState, type JSX } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select } from '@artosera/ui';
import {
  TYPES_TEXTE_STUDIO,
  creationStudioSchema,
  type CreationStudio,
  type FiltresStudio,
} from '@artosera/shared';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { estPorteeObsolete } from '../../auth/identite.js';
import { useTextesStudio, useSourcesStudio, useGesteStudio } from '../../api/studio.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { NotFoundPage } from '../introuvable/NotFoundPage.js';
import { erreurStudio, useDisponibiliteStudio } from './commun.js';
import styles from './Studio.module.css';
import { Verrouillable } from '../module/LectureSeule.js';

export function StudioPage(): JSX.Element {
  const { galerie, compte, galerieSlug } = useShellContext();
  if (!galerieAModule(galerie, 'studio-contenu') || !galerieALaction(galerie, 'studio:read'))
    return <NotFoundPage />;
  return (
    <Liste
      key={`${compte.id}:${galerieSlug}:${galerie.actions.join(',')}:${galerie.modules?.join(',')}`}
    />
  );
}

function Liste(): JSX.Element {
  const { t } = useTranslation('regie'),
    { galerieSlug, galerie } = useShellContext(),
    navigate = useNavigate();
  const [filtres, setFiltres] = useState<FiltresStudio>({ page: 1 });
  const textes = useTextesStudio(galerieSlug, filtres);
  const filtre = (cle: string, valeur: string) =>
    setFiltres({ ...filtres, [cle]: valeur || undefined, page: 1 });
  return (
    <div className={styles.page}>
      <header className={styles.entete}>
        <div>
          <h1>{t('studio.titre')}</h1>
          <p>{t('studio.description')}</p>
        </div>
        <Verrouillable action="studio:write" ouvert={galerieALaction(galerie, 'studio:write')}>
          <Button onClick={() => void navigate(`/${galerieSlug}/studio/nouveau`)}>
            {t('studio.nouveau')}
          </Button>
        </Verrouillable>
      </header>
      <div className={styles.filtres}>
        <Field htmlFor="studio-recherche" label={t('studio.recherche')}>
          <Input
            id="studio-recherche"
            type="search"
            maxLength={120}
            value={filtres.q ?? ''}
            onChange={(e) => filtre('q', e.target.value)}
          />
        </Field>
        {(['type', 'etat', 'langue', 'source'] as const).map((cle) => (
          <Field key={cle} htmlFor={`studio-${cle}`} label={t(`studio.${cle}`)}>
            <Select
              id={`studio-${cle}`}
              value={filtres[cle] ?? 'tous'}
              onValueChange={(v) => filtre(cle, v === 'tous' ? '' : v)}
              options={[
                { value: 'tous', label: t('studio.tous') },
                ...(cle === 'type'
                  ? TYPES_TEXTE_STUDIO
                  : cle === 'etat'
                    ? ['brouillon', 'valide', 'archive']
                    : cle === 'langue'
                      ? ['fr', 'en']
                      : ['libre', 'exposition', 'oeuvres', 'artiste']
                ).map((value) => ({ value, label: t(`studio.${cle}s.${value}`) })),
              ]}
            />
          </Field>
        ))}
      </div>
      {textes.isPending && <p>{t('studio.chargement')}</p>}
      {textes.isError && <p role="alert">{t('studio.erreur')}</p>}
      {textes.data && (
        <>
          <p>{t('studio.total', { count: textes.data.total })}</p>
          <ul className={styles.liste}>
            {textes.data.textes.map((texte) => (
              <li key={texte.id}>
                <Link
                  className={styles.carte}
                  to={`/${galerieSlug}/studio/${texte.id}`}
                  aria-labelledby={`studio-texte-${texte.id}`}
                >
                  <span id={`studio-texte-${texte.id}`}>{texte.titreInterne}</span>
                  <p>
                    {t(`studio.types.${texte.type}`)} · {t(`studio.etats.${texte.etat}`)} ·{' '}
                    {texte.langue.toUpperCase()} · {t(`studio.sources.${texte.source.type}`)}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
          {textes.data.total === 0 && <p>{t('studio.vide')}</p>}
          {textes.data.total > 50 && (
            <div className={styles.actions}>
              <Button
                disabled={(filtres.page ?? 1) === 1}
                onClick={() => setFiltres({ ...filtres, page: (filtres.page ?? 1) - 1 })}
              >
                {t('studio.precedent')}
              </Button>
              <span>{filtres.page ?? 1}</span>
              <Button
                disabled={(filtres.page ?? 1) * 50 >= textes.data.total}
                onClick={() => setFiltres({ ...filtres, page: (filtres.page ?? 1) + 1 })}
              >
                {t('studio.suivant')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function NouveauStudioPage(): JSX.Element {
  const { galerie, compte, galerieSlug } = useShellContext();
  if (!galerieAModule(galerie, 'studio-contenu') || !galerieALaction(galerie, 'studio:write'))
    return <NotFoundPage />;
  return (
    <Nouveau
      key={`${compte.id}:${galerieSlug}:${galerie.actions.join(',')}:${galerie.modules?.join(',')}`}
    />
  );
}

function Nouveau(): JSX.Element {
  const { t, i18n } = useTranslation('regie'),
    { galerieSlug } = useShellContext(),
    navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState<CreationStudio>({
    type: 'communique',
    titreInterne: '',
    source: params.get('exposition')
      ? { type: 'exposition', ids: [params.get('exposition')!] }
      : { type: 'libre', ids: [] },
    brief: '',
    langue: i18n.language === 'en' ? 'en' : 'fr',
    longueur: 'moyenne',
    mentionnerPrixPublics: false,
  });
  const [q, setQ] = useState(''),
    [erreur, setErreur] = useState<string | null>(null);
  const [choix, setChoix] = useState<
    { id: string; libelle: string | { fr: string; en?: string } }[]
  >([]);
  const sources = useSourcesStudio(
      galerieSlug,
      form.source.type,
      q,
      form.source.type === 'exposition' ? form.source.ids[0] : undefined,
    ),
    geste = useGesteStudio(galerieSlug),
    { raison, disponible } = useDisponibiliteStudio();
  const options = [
    ...(sources.data?.sources ?? []),
    ...choix.filter(
      (s) =>
        form.source.ids.includes(s.id) &&
        !sources.data?.sources.some((visible) => visible.id === s.id),
    ),
  ];
  return (
    <div className={styles.page}>
      <Link to={`/${galerieSlug}/studio`}>{t('studio.retour')}</Link>
      <h1>{t('studio.nouveau')}</h1>
      <p>{t('studio.confidentialite')}</p>
      {raison && (
        <p role="status" className={styles.notice}>
          {raison}
        </p>
      )}
      {erreur && <p role="alert">{erreur}</p>}
      <form
        className={styles.formulaire}
        onSubmit={async (e) => {
          e.preventDefault();
          setErreur(null);
          if (!creationStudioSchema.safeParse(form).success) {
            setErreur(t('studio.formulaireInvalide'));
            return;
          }
          try {
            const r = await geste.mutateAsync({ creation: form });
            void navigate(`/${galerieSlug}/studio/${r.texte.id}`);
          } catch (e) {
            if (!estPorteeObsolete(e)) setErreur(erreurStudio(e, t));
          }
        }}
      >
        <label>
          {t('studio.titreInterne')}
          <input
            required
            maxLength={120}
            value={form.titreInterne}
            onChange={(e) => setForm({ ...form, titreInterne: e.target.value })}
          />
        </label>
        <label>
          {t('studio.type')}
          <select
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value as CreationStudio['type'] })}
          >
            {TYPES_TEXTE_STUDIO.map((v) => (
              <option value={v} key={v}>
                {t(`studio.types.${v}`)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('studio.source')}
          <select
            value={form.source.type}
            onChange={(e) => {
              setChoix([]);
              setQ('');
              setForm({
                ...form,
                source: { type: e.target.value as CreationStudio['source']['type'], ids: [] },
              });
            }}
          >
            {['libre', 'exposition', 'oeuvres', 'artiste'].map((v) => (
              <option key={v} value={v}>
                {t(`studio.sources.${v}`)}
              </option>
            ))}
          </select>
        </label>
        {form.source.type !== 'libre' && (
          <>
            <label>
              {t('studio.rechercheSource')}
              <input value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <label>
              {t('studio.selectionSource')}
              <select
                multiple={form.source.type === 'oeuvres'}
                value={
                  form.source.type === 'oeuvres' ? form.source.ids : (form.source.ids[0] ?? '')
                }
                onChange={(e) => {
                  const ids = Array.from(e.target.selectedOptions, (o) => o.value).filter(Boolean);
                  setChoix(options.filter((s) => ids.includes(s.id)));
                  setForm({
                    ...form,
                    source: {
                      ...form.source,
                      ids,
                    },
                  });
                }}
              >
                <option value="">{t('studio.choisir')}</option>
                {options.map((s) => (
                  <option key={s.id} value={s.id}>
                    {typeof s.libelle === 'string'
                      ? s.libelle
                      : s.libelle[form.langue] || s.libelle.fr}
                  </option>
                ))}
              </select>
            </label>
            {form.source.ids.length > 0 && (
              <small>{t('studio.sourcesSelectionnees', { count: form.source.ids.length })}</small>
            )}
            {sources.isError && <p role="alert">{t('studio.erreur')}</p>}
          </>
        )}
        <label>
          {t('studio.brief')}
          <textarea
            maxLength={2000}
            value={form.brief}
            onChange={(e) => setForm({ ...form, brief: e.target.value })}
          />
        </label>
        <label>
          {t('studio.langue')}
          <select
            value={form.langue}
            onChange={(e) => setForm({ ...form, langue: e.target.value as 'fr' | 'en' })}
          >
            <option value="fr">{t('studio.langues.fr')}</option>
            <option value="en">{t('studio.langues.en')}</option>
          </select>
        </label>
        <label>
          {t('studio.longueur')}
          <select
            value={form.longueur}
            onChange={(e) =>
              setForm({ ...form, longueur: e.target.value as CreationStudio['longueur'] })
            }
          >
            {['courte', 'moyenne', 'longue'].map((v) => (
              <option key={v} value={v}>
                {t(`studio.longueurs.${v}`)}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>
            <input
              type="checkbox"
              checked={form.mentionnerPrixPublics}
              onChange={(e) => setForm({ ...form, mentionnerPrixPublics: e.target.checked })}
            />{' '}
            {t('studio.prixPublics')}
          </span>
        </label>
        <Button type="submit" disabled={geste.isPending || !disponible}>
          {geste.isPending ? t('studio.enCours') : t('studio.rediger')}
        </Button>
      </form>
    </div>
  );
}
